import { type Account, type ImplicitAccount, type Network } from "@umami/chains";
import { walletKit } from "@umami/state";
import { WalletConnectError, WcErrorCode, isMultichainEnabled } from "@umami/utils";
import { type SessionTypes } from "@walletconnect/types";

import { type EthSendTransactionParams } from "./type";
import { type EthSignTransactionParams, isEthSignTransactionParams } from "./typeGuards";

export const validateMultichainSupport = (session: SessionTypes.Struct, method: string) => {
  if (!isMultichainEnabled()) {
    throw new WalletConnectError(
      "Chain support is not enabled",
      WcErrorCode.METHOD_UNSUPPORTED,
      session,
      method
    );
  }
};

export const validatePersonalSignParams = (
  params: unknown,
  session: SessionTypes.Struct
): string[] => {
  if (!Array.isArray(params)) {
    throw new WalletConnectError(
      "Invalid params format for personal_sign",
      WcErrorCode.INVALID_PARAMS,
      session
    );
  }

  return params as string[];
};

export const validateEthSignTypedDataParams = (
  params: unknown,
  session: SessionTypes.Struct
): [string, string] => {
  if (!Array.isArray(params) || params.length !== 2) {
    throw new WalletConnectError(
      "Invalid params format for eth_signTypedData_v4",
      WcErrorCode.INVALID_PARAMS,
      session
    );
  }

  const [address, typedData] = params;

  if (typeof address !== "string") {
    throw new WalletConnectError(
      "Invalid address parameter for eth_signTypedData_v4",
      WcErrorCode.INVALID_PARAMS,
      session
    );
  }

  if (typeof typedData !== "string") {
    throw new WalletConnectError(
      "Invalid typedData parameter for eth_signTypedData_v4",
      WcErrorCode.INVALID_PARAMS,
      session
    );
  }

  return [address, typedData];
};

export const validateEthSignTransactionParams = (
  params: unknown,
  session: SessionTypes.Struct
): [EthSignTransactionParams] => {
  if (!Array.isArray(params) || params.length !== 1) {
    throw new WalletConnectError(
      "Invalid params format for eth_signTransaction",
      WcErrorCode.INVALID_PARAMS,
      session
    );
  }

  const [transaction] = params;

  if (!transaction || typeof transaction !== "object") {
    throw new WalletConnectError(
      "Invalid transaction parameter for eth_signTransaction",
      WcErrorCode.INVALID_PARAMS,
      session
    );
  }

  // Use type guard to validate the transaction structure
  if (!isEthSignTransactionParams(transaction)) {
    throw new WalletConnectError(
      "Missing or invalid 'from' field in eth_signTransaction params",
      WcErrorCode.INVALID_PARAMS,
      session
    );
  }

  return [transaction];
};

export const validateSession = (topic: string) => {
  const wcPeers = walletKit.getActiveSessions();

  if (!(topic in wcPeers)) {
    throw new WalletConnectError(`Unknown session ${topic}`, WcErrorCode.SESSION_NOT_FOUND, null);
  }

  return wcPeers[topic];
};

export const validateMessage = (message: string | undefined, session: SessionTypes.Struct) => {
  if (!message) {
    throw new WalletConnectError("Missing message in request", WcErrorCode.INVALID_PARAMS, session);
  }

  return message;
};
export const validateAccount = (account: string | undefined, session: SessionTypes.Struct) => {
  if (!account) {
    throw new WalletConnectError(
      "Missing account in request",
      WcErrorCode.MISSING_ACCOUNT_IN_REQUEST,
      session
    );
  }

  return account;
};

export const validateNetwork = (
  chainFullId: string,
  session: SessionTypes.Struct,
  findNetworkByChainFullId: (chainFullId: string) => Network | undefined
) => {
  const network = findNetworkByChainFullId(chainFullId);

  if (!network) {
    throw new WalletConnectError(
      `Unsupported network ${chainFullId}`,
      WcErrorCode.UNSUPPORTED_CHAINS,
      session,
      chainFullId
    );
  }

  return network;
};

export const validateImplicitAccount = (
  account: Account | undefined,
  accountPkh: string,
  session: SessionTypes.Struct
) => {
  if (!account) {
    throw new WalletConnectError(
      `Unknown account, no signer: ${accountPkh}`,
      WcErrorCode.INTERNAL_SIGNER_IS_MISSING,
      session
    );
  }

  if (!("pk" in account)) {
    throw new WalletConnectError(
      `Account is not an implicit account: ${accountPkh}`,
      WcErrorCode.INTERNAL_SIGNER_IS_MISSING,
      session
    );
  }

  return account as ImplicitAccount;
};

export const validateEthSendTransactionParams = (
  params: unknown,
  session: SessionTypes.Struct
): EthSendTransactionParams => {
  if (!Array.isArray(params) || params.length === 0) {
    throw new WalletConnectError(
      "Invalid params format for eth_sendTransaction",
      WcErrorCode.INVALID_PARAMS,
      session
    );
  }

  const txParams = params[0] as EthSendTransactionParams;

  if (!txParams.from || !txParams.to) {
    throw new WalletConnectError(
      "Missing required fields in eth_sendTransaction params",
      WcErrorCode.INVALID_PARAMS,
      session
    );
  }

  return txParams;
};

export const validateWalletSwitchEthereumChainParams = (
  params: unknown,
  session: SessionTypes.Struct
): { chainId: string } => {
  if (!Array.isArray(params) || params.length === 0) {
    throw new WalletConnectError(
      "Invalid params format for wallet_switchEthereumChain",
      WcErrorCode.INVALID_PARAMS,
      session
    );
  }

  const switchParams = params[0] as { chainId: string };

  if (!switchParams.chainId) {
    throw new WalletConnectError(
      "Missing chainId in wallet_switchEthereumChain params",
      WcErrorCode.INVALID_PARAMS,
      session
    );
  }

  return switchParams;
};

export const validateEthAccountsParams = (params: unknown, session: SessionTypes.Struct): void => {
  if (!Array.isArray(params) || params.length !== 0) {
    throw new WalletConnectError(
      "eth_accounts does not accept any parameters",
      WcErrorCode.INVALID_PARAMS,
      session
    );
  }
};
